/* eslint-disable */
// @ts-nocheck

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  ContainerQueryParams,
  useContainerQuery,
} from '@/hooks/container/useContainerQuery'
import { Container } from '@/interfaces/container'
import { storage } from '@/lib/utils'
import { useAppSelector } from '@/store/hooks'

import {
  ContainerFilters,
  useContainerFilters,
} from '../filters/hooks/useContainerFilters'

// Define a more complete interface for invoice data
interface ContainerData {
  items: Container[]
  totalItems: number
  currentPage: number
  totalPages: number
}

// Define the context type
interface TableContextType {
  currentPage: number
  setCurrentPage: (page: number) => void
  isFetching: boolean
  isError: boolean
  pageSize: number
  setPageSize: (size: number) => void
  containerData: ContainerData | null
  isLoading: boolean
  refetch: () => void
  // Add direct access to pagination methods for convenience
  goToNextPage: () => void
  goToPreviousPage: () => void
  // Skeleton data for loading state
  skeletonRowCount: number
  // Additional helpful properties
  hasNextPage: boolean
  hasPreviousPage: boolean
  // Filter management
  resetToFirstPage: () => void
}

// Create the context with a default value
const TableContext = createContext<TableContextType | undefined>(undefined)

// Default values
const INITIAL_PAGE = 1
const INITIAL_PAGE_SIZE = 50

// Storage keys
const STORAGE_KEYS = {
  CURRENT_PAGE: 'billing-table-current-page',
  PAGE_SIZE: 'billing-table-page-size',
} as const

// Helper to create a filter key for comparison
const createFilterKey = (filters: ContainerFilters): string => {
  return JSON.stringify({
    search: filters.search || '',
    status: [...filters.status].sort(),
    category: [...filters.category].sort(),
    isReturned: filters.isReturned || '',
    ownerCompanyId: filters.ownerCompanyId || '',
    logisticCompanyId: filters.logisticCompanyId || '',
    transporterCompanyId: filters.transporterCompanyId || '',
    storageCompanyId: filters.storageCompanyId || '',
  })
}

// Provider component that will wrap the table components
export const TableContextProvider = ({ children }: { children: ReactNode }) => {
  // Get filters from the billing filters hook (now with built-in localStorage persistence)
  const { filters } = useContainerFilters()

  // Justo después de obtener filters, fuerza el valor por defecto si está vacío
  useEffect(() => {
    if (!filters.status || filters.status.length === 0) {
      filters.status = ['CREATED']
    }
  }, [filters])

  const { business } = useAppSelector((state) => state.init)

  // Track previous filter state to detect changes
  const previousFiltersRef = useRef<string>('')
  const isInitialMount = useRef(true)

  // Initialize state from localStorage
  const [currentPage, setCurrentPageState] = useState(() =>
    storage.get(STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE)
  )

  const [pageSize, setPageSizeState] = useState(() =>
    storage.get(STORAGE_KEYS.PAGE_SIZE, INITIAL_PAGE_SIZE)
  )

  // Check if filters have changed (excluding pagination-related changes)
  const currentFilterKey = createFilterKey(filters)
  const filtersHaveChanged =
    previousFiltersRef.current !== currentFilterKey && !isInitialMount.current

  // Update previous filters reference
  useEffect(() => {
    previousFiltersRef.current = currentFilterKey
    isInitialMount.current = false
  }, [currentFilterKey])

  // Reset to page 1 when filters change (but not on initial mount)
  useEffect(() => {
    if (filtersHaveChanged && currentPage !== INITIAL_PAGE) {
      setCurrentPageState(INITIAL_PAGE)
      storage.set(STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE)
    }
  }, [filtersHaveChanged, currentPage])

  // Build query parameters from current state and filters
  // Solo enviamos al backend los filtros de estado y empresas
  const queryParams: ContainerQueryParams = {
    page: currentPage,
    per_page: pageSize,
    status:
      filters.status &&
      filters.status.length === 1 &&
      !filters.status.includes('all')
        ? filters.status[0]
        : null,
    ownerCompanyId:
      filters.ownerCompanyId && filters.ownerCompanyId !== ''
        ? filters.ownerCompanyId
        : null,
    managerCompanyId:
      filters.logisticCompanyId && filters.logisticCompanyId !== ''
        ? filters.logisticCompanyId
        : null,
    transporterCompanyId:
      filters.transporterCompanyId && filters.transporterCompanyId !== ''
        ? filters.transporterCompanyId
        : null,
    storageCompanyId:
      filters.storageCompanyId && filters.storageCompanyId !== ''
        ? filters.storageCompanyId
        : null,
  }

  // Use the invoice query hook with the current parameters
  const {
    data: containerData,
    isLoading,
    isFetching,
    isError,
    isPending,
    isRefetching,
    refetch,
    pagination,
  } = useContainerQuery({
    params: queryParams,
    options: {
      staleTime: 5 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: true,
    },
    businessId: business?.id ?? 0,
  })

  // Handle page changes with localStorage persistence
  const setCurrentPage = useCallback((page: number) => {
    setCurrentPageState(page)
    storage.set(STORAGE_KEYS.CURRENT_PAGE, page)
  }, [])

  // Handle page size changes with localStorage persistence
  const setPageSize = useCallback(
    (size: number) => {
      setPageSizeState(size)
      storage.set(STORAGE_KEYS.PAGE_SIZE, size)

      // Reset to first page when changing page size (if not already on page 1)
      if (currentPage !== INITIAL_PAGE) {
        setCurrentPage(INITIAL_PAGE)
      }
    },
    [currentPage, setCurrentPage]
  )

  // Navigation helpers
  const goToNextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      setCurrentPage(currentPage + 1)
    }
  }, [currentPage, setCurrentPage, pagination.hasNextPage])

  const goToPreviousPage = useCallback(() => {
    if (pagination.hasPreviousPage) {
      setCurrentPage(currentPage - 1)
    }
  }, [currentPage, setCurrentPage, pagination.hasPreviousPage])

  // Helper to reset to first page (useful for external components)
  const resetToFirstPage = useCallback(() => {
    if (currentPage !== INITIAL_PAGE) {
      setCurrentPage(INITIAL_PAGE)
    }
  }, [currentPage, setCurrentPage])

  // Sync with API response if there's a mismatch (fallback safety)
  useEffect(() => {
    if (
      containerData?.currentPage &&
      containerData.currentPage !== currentPage
    ) {
      setCurrentPageState(containerData.currentPage)
      storage.set(STORAGE_KEYS.CURRENT_PAGE, containerData.currentPage)
    }
  }, [containerData?.currentPage, currentPage])

  // Aplicar filtros locales a los datos recibidos del backend
  const filteredData = useMemo(() => {
    if (!containerData) return null

    let filteredItems = [...containerData.items]

    // Filtrar por categoría
    if (
      filters.category &&
      filters.category.length === 1 &&
      !filters.category.includes('all')
    ) {
      const categoryId = filters.category[0]
      filteredItems = filteredItems.filter((container) => {
        // Las categorías son strings en el modelo de datos
        return container.categories.some((category: any) => {
          // Si la categoría es un string, comparamos directamente
          if (typeof category === 'string') {
            return category === categoryId
          }
          // Si la categoría es un objeto, comparamos su id
          return String(category.id) === categoryId
        })
      })
    }

    // Filtrar por isReturned
    if (filters.isReturned !== 'all') {
      const isReturned = filters.isReturned === 'true'
      filteredItems = filteredItems.filter(
        (container) => container.isReturned === isReturned
      )
    }

    // Filtrar por búsqueda (en el nombre o número de operación)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filteredItems = filteredItems.filter((container) => {
        const nameMatch = container.name.toLowerCase().includes(searchLower)
        const operationMatch = String(container.operationNumber).includes(
          filters.search
        )
        return nameMatch || operationMatch
      })
    }

    return {
      ...containerData,
      items: filteredItems,
      // No actualizamos totalItems ni totalPages ya que son para la paginación del backend
    }
  }, [containerData, filters.category, filters.isReturned, filters.search])

  // Create the context value object
  const contextValue: TableContextType = {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    containerData: filteredData || null,
    isLoading: isLoading || isPending || isRefetching,
    isFetching,
    isError,
    refetch,
    goToNextPage,
    goToPreviousPage,
    skeletonRowCount: pageSize,
    hasNextPage: pagination.hasNextPage,
    hasPreviousPage: pagination.hasPreviousPage,
    resetToFirstPage,
  }

  return (
    <TableContext.Provider value={contextValue}>
      {children}
    </TableContext.Provider>
  )
}

// Custom hook to use the table context
export const useTableContext = () => {
  const context = useContext(TableContext)
  if (context === undefined) {
    throw new Error(
      'useTableContext must be used within a TableContextProvider'
    )
  }
  return context
}

// Export storage utilities for external use if needed
export const tableStorage = {
  clearAll: () => {
    Object.values(STORAGE_KEYS).forEach((key) => storage.remove(key))
  },
  getSavedPage: () => storage.get(STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE),
  getSavedPageSize: () =>
    storage.get(STORAGE_KEYS.PAGE_SIZE, INITIAL_PAGE_SIZE),
}
